// @ts-check
import { followCamera } from '../engine/camera.js';
import { WORLD, MOTION } from '../game/tuning.js';
import { playerBody } from '../game/power.js';
import { supportHeight } from '../game/world.js';
import { animate, createPoseRenderer, renderPose, trickPose } from './character/directional.js';
import { createRunRenderer, prepareRunSource, RUN_STEP } from './character/run-cycle.js';
import { createWorldRenderer } from './background.js';
import { createLevelRenderer } from './terrain.js';
import { createEffects } from './effects.js';
import { createUplinkRenderer, uplinkVisual } from './uplink.js';
import { createReactions } from './character/reactions.js';
import { createFeedback } from './feedback.js';

/** Presentation owns its camera, poses, particles, and Canvas resources.
 * Simulation receives no references to this renderer or the DOM.
 * @param {import('../ui/dom.js').GameElements} view @param {()=>void} ready
 */
export function createScene(view, ready) {
  const { qp, pose, image, runCanvas, poseCanvas, sources, stage, shadow, impulse } = view;
  const reactions = createReactions(view, ready);
  const run = createRunRenderer(runCanvas, sources.run, sources.original, ready);
  const poses = createPoseRenderer(poseCanvas, sources.right, sources.left, prepareRunSource, ready);
  const background = createWorldRenderer(view.background);
  const terrain = createLevelRenderer(view.terrain);
  const effects = createEffects();
  const feedback = createFeedback(view.root);
  const uplink = createUplinkRenderer(view.uplinkCanvas);
  const animation = { distance: 0, facing: 1, landing: 0 };
  let poseIndex = 0;
  let cameraX = 0;
  let cameraY = 0;
  let scale = 1;
  let floorInset = 0;
  let sceneView = { width: 1, height: 1, floor: 1, scale: 1 };
  /** @param {import('../game/session.js').Session} session */
  function follow(session) {
    const levelMode = session.mode === 'level';
    const bodyWidth = levelMode ? playerBody(session.player).width : qp.offsetWidth;
    const focusX = session.transfer?.phase === 'departing' ? uplinkVisual(session, false, bodyWidth).x : session.player.x;
    const arena = levelMode && session.level.boss && session.player.x >= session.level.boss.definition.left;
    const lookAhead = levelMode && !session.transfer ? session.player.facing * Math.min(arena ? 144 : 84, stage.clientWidth / scale * (arena ? 0.4 : 0.25)) : 0;
    cameraX = followCamera(focusX + lookAhead, cameraX, stage.clientWidth / scale, bodyWidth);
    const artHeight = levelMode ? WORLD.artHeight * session.player.size : qp.offsetHeight;
    cameraY = Math.max(0, session.player.y + artHeight - (stage.clientHeight - floorInset) / scale * 0.78);
    if (levelMode && !session.transfer) for (const [i, item] of (session.level.definition.transistors ?? []).entries()) {
      if (session.level.transistorsFound & (1 << (item.slot ?? i))) continue;
      const near = Math.max(0, 1 - Math.abs(item.x - session.player.x - bodyWidth / 2) / 180);
      const frame = Math.max(0, Math.min(session.player.y, item.y + 28 - (stage.clientHeight - floorInset) / scale * .9));
      cameraY = Math.max(cameraY, frame * near);
    }
    if (levelMode) cameraX = Math.max(0, Math.min(Math.max(0, session.level.width - stage.clientWidth / scale), cameraX));
  }
  return {
    /** @param {import('../game/types.js').GameEvent} event */
    accept(event) {
      reactions.accept(event);
      effects.accept(event);
      feedback.accept(event);
      background.accept(event);
      if (event.type === 'restarted') { cameraX = cameraY = 0; poseIndex = 0; animation.distance = animation.landing = 0; animation.facing = 1; }
    },
    /** @param {import('../game/session.js').Session} session */
    resize(session) {
      const levelMode = session.mode === 'level';
      // Keep the ground low. Extend the actor's clip through the control strip
      // instead of moving the whole floor up to make room for a fall.
      floorInset = levelMode ? Math.min(24, stage.clientHeight * 0.04) : 0;
      scale = levelMode ? Math.max(0.4, Math.min(2.5, stage.clientWidth / (WORLD.tile * 10), (stage.clientHeight - floorInset - 8) / (WORLD.tile * 8))) : 1;
      for (const element of [qp, shadow, impulse]) element.style.bottom = `${2 + floorInset}px`;
      stage.style.setProperty('--floor-inset', `${floorInset}px`);
      stage.style.setProperty('--fall-overflow', `${Math.max(0, window.innerHeight - stage.getBoundingClientRect().bottom)}px`);
      qp.style.height = levelMode ? `${WORLD.artHeight * session.player.size * scale}px` : '';
      session.resizeFree(stage.clientWidth - qp.offsetWidth - 12, stage.clientHeight - qp.offsetHeight - 8);
      follow(session);
      sceneView = { width: window.innerWidth, height: window.innerHeight, floor: (stage.getBoundingClientRect().bottom || stage.clientHeight) - 2 - floorInset, scale };
      background.resize(sceneView);
    },
    /** @param {import('../game/session.js').Session} session @param {number} dt @param {number} traveled @param {boolean} wasAirborne */
    step(session, dt, traveled, wasAirborne) {
      reactions.step(session.player, dt);
      poseIndex = animate(session.player, animation, dt, traveled, wasAirborne, session.mode === 'level' ? WORLD.artHeight * session.player.size : qp.offsetHeight);
      animation.facing = session.player.facing;
      effects.step(dt); feedback.step(dt, session); background.step(dt); follow(session);
    },
    pause() { animation.distance = animation.landing = 0; poseIndex = 0; },
    interruptIdle() { reactions.interrupt(); },
    /** @param {import('../game/session.js').Session} session @param {boolean} reduced @param {boolean} sprint @param {string} [flow] @param {boolean} [cosmeticPaused] @param {boolean} [engaged] */
    render(session, reduced, sprint, flow = 'playing', cosmeticPaused = false, engaged = false) {
      const state = session.player;
      const levelMode = session.mode === 'level';
      const bodyWidth = levelMode ? playerBody(state).width : qp.offsetWidth;
      const exit = uplinkVisual(session, reduced, bodyWidth);
      qp.style.height = levelMode ? `${WORLD.artHeight * state.size * scale}px` : '';
      qp.dataset.power = state.power; qp.dataset.overclock = String(state.overclock > 0);
      qp.dataset.invulnerable = String(state.invulnerable > 0);
      qp.style.opacity = String(exit.opacity * (!session.transfer && !reduced && state.invulnerable > 0 && Math.floor(state.invulnerable * 10) % 2 ? 0.45 : 1));
      qp.style.clipPath = exit.dissolve ? `inset(0 0 ${exit.dissolve * 100}% 0)` : '';
      const screenX = levelMode ? (exit.x - cameraX + bodyWidth / 2) * scale - qp.offsetWidth / 2 : state.x - cameraX + 6;
      qp.style.transform = `translate(${screenX}px, ${-(exit.y - cameraY) * scale}px)${session.transfer ? ` scale(${exit.scale})` : ''}`;
      qp.dataset.transfer = session.transfer?.phase ?? '';
      qp.dataset.cameraY = String(cameraY);
      qp.dataset.worldX = String(state.x); qp.dataset.worldY = String(state.y); qp.dataset.grounded = String(state.grounded);
      qp.dataset.facing = animation.facing < 0 ? 'left' : 'right';
      const selectedPose = session.transfer?.quantum ? 12 : reduced || (levelMode && !session.level.started) ? 0 : trickPose(state, poseIndex);
      renderPose(pose, image, selectedPose, animation.facing);
      const movingOnFoot = state.grounded && Math.abs(state.vx) > 10 && !state.crouching && !state.sliding && !state.impact;
      const running = !session.transfer?.quantum && !reduced && (movingOnFoot || exit.walking);
      const exitFacing = session.transfer?.phase === 'departing' && session.transfer.gate - bodyWidth / 2 < session.transfer.x ? -1 : 1;
      const renderedRun = running && run.render(session.transfer ? Math.floor(exit.age * 12) % 4 : Math.floor(animation.distance / RUN_STEP) % 4, session.transfer ? exitFacing : animation.facing);
      const renderedTransfer = reactions.renderTransfer(session.transfer);
      const renderedIdle = reactions.render(session, flow, cosmeticPaused || flow === 'paused', reduced, engaged);
      // Keep the outgoing, registered pose under the idle entrance. CSS blends
      // the two layers; reuse its painted canvas while idle instead of repainting.
      const renderedPose = !renderedRun && (renderedIdle ? !poseCanvas.hidden : poses.render(selectedPose, animation.facing));
      runCanvas.hidden = renderedTransfer || !renderedRun; poseCanvas.hidden = renderedTransfer || !renderedPose; pose.hidden = renderedTransfer || renderedRun || renderedPose;
      qp.dataset.animation = renderedTransfer ? 'quantum-transfer' : renderedIdle ? 'idle' : renderedRun ? 'run' : renderedPose ? 'sprite' : 'pose'; pose.dataset.walking = String(running);
      const trick = state.pound !== 'none' || state.impact ? 'ground-pound' : state.boost ? 'double-jump'
        : state.sliding ? 'slide' : state.crouching ? 'crouch' : movingOnFoot && sprint ? 'sprint' : '';
      qp.dataset.trick = trick;
      impulse.hidden = reduced || !(state.boost || state.impact);
      const progress = state.impact ? 1 - state.impact / MOTION.impactTime : 1 - state.boost / MOTION.boostTime;
      impulse.style.transform = `translate(${screenX + qp.offsetWidth / 2 - 36}px, ${-(state.y - cameraY) * scale}px) scale(${(levelMode ? scale * 0.5 : 1) * (1 + progress * 1.3)})`;
      impulse.style.opacity = String((1 - progress) * 0.65);
      const surface = levelMode ? supportHeight(session.level, state) : 0;
      shadow.hidden = surface === null;
      shadow.style.opacity = String(0.12 * exit.opacity);
      const shadowScale = reduced ? 1 : 1 - Math.min(state.y / 500, 0.35);
      shadow.style.transform = `translate(${screenX + qp.offsetWidth / 2 - 44}px, ${-((surface ?? 0) - cameraY) * scale}px) scale(${shadowScale * (levelMode ? scale * 0.45 : 1)})`;
      background.setWorld(levelMode ? session.level.definition.world ?? 1 : 1);
      background.render(cameraX * scale, reduced, cameraY * scale);
      if (levelMode) terrain.render(session.level, cameraX, { ...sceneView, floor: sceneView.floor + cameraY * scale }, reduced, effects.sparks);
      feedback.render(cameraX, { ...sceneView, floor: sceneView.floor + cameraY * scale }, reduced, levelMode);
      uplink.render(session.transfer, { width: stage.clientWidth, height: stage.clientHeight,
        x: screenX + qp.offsetWidth / 2, feet: stage.clientHeight - 2 - floorInset - (exit.y - cameraY) * scale,
        scale, artHeight: WORLD.artHeight * state.size }, reduced);
      view.transferCover.hidden = !session.transfer;
      view.transferCover.style.opacity = String(exit.cover);
      return { 'ground-pound': 'Ground pound', 'double-jump': 'Double jump', slide: 'Slide', crouch: 'Crouch', sprint: 'Sprint', '': '' }[trick];
    },
    get active() { return background.active || reactions.active || animation.landing > 0 || feedback.active; },
    dispose() { reactions.dispose(); feedback.dispose(); run.dispose(); poses.dispose(); background.dispose(); },
  };
}
